import axiosInstance from '../axiosConfig';
import { AuthResponse, LoginCredentials, RegisterData, Member } from '../../types';

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */
class AuthService {
  /**
   * Login member with email and password
   * @param credentials - Login credentials (email, password)
   * @returns Authentication response with token and member data
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await axiosInstance.post<any>('/members/login', credentials);
    // Backend returns { access_token, data, message, success }
    // Transform to match our AuthResponse interface
    return {
      accessToken: response.data.access_token,
      member: response.data.data
    };
  }

  /**
   * Register new member
   * @param data - Registration data
   * @returns Authentication response with token and member data
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await axiosInstance.post<any>('/members/register', data);
    // Backend returns { access_token, data, message, success }
    // Transform to match our AuthResponse interface
    return {
      accessToken: response.data.access_token,
      member: response.data.data
    };
  }

  /**
   * Get current authenticated member
   * @returns Current member data
   */
  async getCurrentUser(): Promise<Member> {
    const response = await axiosInstance.get<any>('/members/me');
    // Backend returns { success, data }
    return response.data.data;
  }

  /**
   * Logout member
   * Clears local storage and authentication state
   */
  async logout(): Promise<void> {
    // H6: clear the httpOnly cookie server-side (JS can't delete it directly).
    try {
      await axiosInstance.post('/members/logout');
    } catch {
      // Ignore network/auth errors — we still clear local state below.
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
  }

  /**
   * Check if user is authenticated
   * @returns True if user has valid token
   */
  isAuthenticated(): boolean {
    // H6: the token is in an httpOnly cookie (not JS-readable); the cached user is
    // the synchronous signal. A stale flag self-corrects on the first 401.
    return !!localStorage.getItem('user');
  }

  /**
   * Get stored access token
   * @returns Access token or null
   */
  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }
}

const authService = new AuthService();
export default authService;
